import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MessageCircle, Trash2, Lock, Clock } from "lucide-react";

function groupByDate(convs) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86400000;
  const buckets = { Today: [], Yesterday: [], "Previous 7 Days": [], Earlier: [] };
  for (const c of convs) {
    const t = c.updated_at ? new Date(c.updated_at).getTime() : 0;
    if (t >= startOfToday) buckets["Today"].push(c);
    else if (t >= startOfToday - dayMs) buckets["Yesterday"].push(c);
    else if (t >= startOfToday - 7 * dayMs) buckets["Previous 7 Days"].push(c);
    else buckets["Earlier"].push(c);
  }
  return Object.entries(buckets).filter(([, items]) => items.length > 0);
}

export default function HistorySheet({ open, onOpenChange, conversations, activeId, onSelect, onDelete, isPro, onUpgrade }) {
  const groups = groupByDate(conversations);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-80 p-0 bg-[#0a0a0f] border-[#2a3040] flex flex-col" data-testid="history-sheet">
        <SheetHeader className="p-5 border-b border-[#2a3040]">
          <SheetTitle className="flex items-center gap-2 text-white font-display">
            <Clock size={18} className="text-[#FF5A00]" /> History
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-3 relative">
          {conversations.length === 0 ? (
            <p className="text-xs text-[#6b7280] px-2 py-4">No conversations yet.</p>
          ) : (
            <div className="relative">
              <div className={!isPro ? "blur-[5px] pointer-events-none select-none" : ""} data-testid="history-list">
                {groups.map(([label, items]) => (
                  <div key={label} className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] font-bold px-2 py-1.5">{label}</div>
                    {items.map((c) => (
                      <div key={c.id} data-testid={`conversation-${c.id}`}
                        className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                          activeId === c.id ? "bg-[#1a1a24]" : "hover:bg-[#15151d]"}`}
                        onClick={() => isPro && onSelect(c.id)}>
                        <MessageCircle size={15} className="text-[#6b7280] shrink-0" />
                        <span className="text-sm text-[#d1d5db] truncate flex-1">{c.title}</span>
                        {isPro && (
                          <button data-testid={`delete-conversation-${c.id}`}
                            onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                            className="text-[#6b7280] hover:text-[#f87171] opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {!isPro && (
                <button data-testid="history-locked-overlay" onClick={onUpgrade}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">
                  <div className="w-10 h-10 rounded-full bg-[#FF5A00]/15 flex items-center justify-center">
                    <Lock size={18} className="text-[#FF5A00]" />
                  </div>
                  <p className="text-xs text-white font-semibold">History is a Pro feature</p>
                  <p className="text-[11px] text-[#9ca3af]">Tap to unlock your full chat history</p>
                </button>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
